import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { restaurantsApi, briefingApi } from '../lib/api';
import { getTodayString, formatCurrency, cn } from '../lib/utils';
import { Sidebar } from '../components/layout/Sidebar';
import { Header } from '../components/layout/Header';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    Sun,
    Moon,
    Cloud,
    CloudRain,
    Thermometer,
    Users,
    Utensils,
    Target,
    Euro,
    AlertTriangle,
    Star,
    XCircle,
    UserCheck,
    Clock,
    ChefHat,
    UtensilsCrossed,
    Calendar,
    RefreshCw,
    Crown,
    Coffee,
    Wine,
    CheckCircle,
    XOctagon,
    TrendingUp,
    ShieldAlert,
    MessageSquareWarning,
    ThumbsDown,
    ExternalLink,
    X
} from 'lucide-react';

// Mapping des restaurants vers les noms d'affichage
const RESTAURANT_DISPLAY_NAMES = {
    "Ma biche sur le toit": "Ma biche sur le toit",
    "Compagnie Française": "Compagnie Française",
    "La Compagnie Française": "Compagnie Française",
    "Compagnie Busca": "Compagnie Busca",
    "Jacques - Balma": "Jacques Balma",
    "Jacques Balma": "Jacques Balma",
    "Café Chouchou": "Café Chouchou",
    "Sous les pins": "Sous les pins",
    "Coup de Bambou": "Coup de Bambou"
};

// Mapping des logos des restaurants
const RESTAURANT_LOGOS = {
    "Ma biche sur le toit": "/logos/ma-biche-sur-le-toit.png",
    "Compagnie Française": "/logos/compagnie-francaise.png",
    "La Compagnie Française": "/logos/compagnie-francaise.png",
    "Compagnie Busca": "/logos/compagnie-busca.png",
    "Jacques - Balma": "/logos/jacques-balma.png",
    "Jacques Balma": "/logos/jacques-balma.png",
    "Jacques Blagnac": "/logos/jacques-balma.png",
    "Café Chouchou": "/logos/cafe-chouchou.png",
    "Sous les pins": "/logos/sous-les-pins.png",
    "Coup de Bambou": "/logos/coup-de-bambou.png",
    "Coup de bambou": "/logos/coup-de-bambou.png"
};

// Sous-titres pour distinguer les restaurants (notamment les Jacques)
const RESTAURANT_SUBTITLES = {
    "Jacques Balma": "Balma",
    "Jacques - Balma": "Balma",
    "Jacques Blagnac": "Blagnac"
};

// Composant météo du jour - Heures de pointe
const WeatherSection = ({ weather }) => {
    if (!weather || !weather.peak_hours) {
        return (
            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                    <Thermometer className="w-5 h-5" />
                    <span>Météo en cours de chargement...</span>
                </div>
            </div>
        );
    }

    const peakHours = [
        { key: 'matin', label: 'Matin (9h)', icon: <Sun className="w-4 h-4 text-orange-400" /> },
        { key: 'midi', label: 'Midi (13h)', icon: <Sun className="w-4 h-4 text-yellow-500" /> },
        { key: 'limonade', label: 'Limo (16h)', icon: <Cloud className="w-4 h-4 text-cyan-400" /> },
        { key: 'soir', label: 'Soir (21h)', icon: <Moon className="w-4 h-4 text-indigo-400" /> }
    ];

    return (
        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                    <Thermometer className="w-5 h-5 text-blue-500" />
                    Météo aux heures de pointe
                </h3>
                <span className="text-sm text-muted-foreground">{weather.location || 'Toulouse'}</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
                {peakHours.map(period => {
                    const data = weather.peak_hours[period.key] || {};
                    return (
                        <div key={period.key} className="text-center p-2 bg-background/50 rounded-lg">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                {period.icon}
                                <span className="text-xs font-medium">{period.label}</span>
                            </div>
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <span className="text-lg">{data.icon || '☀️'}</span>
                                <span className="text-lg font-bold">{data.temperature ?? '--'}°</span>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{data.description || 'N/A'}</p>
                            {data.precipitation_probability > 0 && (
                                <p className="text-xs text-blue-400">
                                    💧 {data.precipitation_probability}%
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Composant VIP dédié
const VIPSection = ({ reservations }) => {
    const hasVips = reservations?.has_vips && reservations?.vips?.length > 0;
    
    return (
        <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
                <Crown className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-amber-700 dark:text-amber-400">
                    VIP du jour {hasVips && `(${reservations.vips.length})`}
                </h3>
            </div>
            
            {hasVips ? (
                <div className="space-y-3">
                    {reservations.vips.map((vip, idx) => (
                        <div key={idx} className="bg-white/50 dark:bg-black/20 rounded-lg p-3 border border-amber-200/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Crown className="w-4 h-4 text-amber-500" />
                                    <span className="font-semibold text-foreground">{vip.name}</span>
                                </div>
                                {vip.visit_count > 0 && (
                                    <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 px-2 py-1 rounded-full font-medium">
                                        {vip.visit_count} visite{vip.visit_count > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {vip.party_size} pers.
                                </span>
                                <span>•</span>
                                <span>{vip.time}</span>
                                <span>•</span>
                                <span className="capitalize">{vip.meal_period === 'midi' ? 'Midi' : 'Soir'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex items-center justify-center py-4 text-muted-foreground">
                    <span className="text-sm italic">Pas de VIP identifié</span>
                </div>
            )}
        </div>
    );
};

// Composant objectifs CA complet (TTC)
const ObjectivesSection = ({ objectives }) => {
    if (!objectives?.has_budget) {
        return (
            <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-emerald-500" />
                    Objectifs du jour
                </h3>
                <p className="text-sm text-muted-foreground">Aucun budget configuré pour cette date</p>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-500" />
                    Objectifs du jour <span className="text-xs text-muted-foreground">(TTC)</span>
                </h3>
                <span className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(objectives.objectif_jour_ttc || 0)}
                </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
                {/* Midi */}
                <div className="bg-background/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <Sun className="w-4 h-4 text-yellow-500" />
                        <span className="font-medium">Midi</span>
                    </div>
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">CA Objectif</span>
                            <span className="font-semibold">{formatCurrency(objectives.objectif_midi_ttc || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">TM budget</span>
                            <span className="font-semibold">{formatCurrency(objectives.tm_midi || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Cvs budget</span>
                            <span className="font-semibold">{objectives.cvs_midi || 0}</span>
                        </div>
                    </div>
                </div>

                {/* Soir */}
                <div className="bg-background/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <Moon className="w-4 h-4 text-indigo-500" />
                        <span className="font-medium">Soir</span>
                    </div>
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">CA Objectif</span>
                            <span className="font-semibold">{formatCurrency(objectives.objectif_soir_ttc || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">TM budget</span>
                            <span className="font-semibold">{formatCurrency(objectives.tm_soir || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-muted-foreground">Cvs budget</span>
                            <span className="font-semibold">{objectives.cvs_soir || 0}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* PDJ / Limo / Night si présent */}
            {(objectives.objectif_pdj_ttc > 0 || objectives.objectif_limo_ttc > 0 || objectives.objectif_night_ttc > 0) && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                    {objectives.objectif_pdj_ttc > 0 && (
                        <div className="bg-background/50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <Coffee className="w-3 h-3 text-amber-600" />
                                <span className="text-xs text-muted-foreground">PDJ</span>
                            </div>
                            <p className="font-semibold text-sm">{formatCurrency(objectives.objectif_pdj_ttc)}</p>
                        </div>
                    )}
                    {objectives.objectif_limo_ttc > 0 && (
                        <div className="bg-background/50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <Coffee className="w-3 h-3 text-orange-500" />
                                <span className="text-xs text-muted-foreground">Limo</span>
                            </div>
                            <p className="font-semibold text-sm">{formatCurrency(objectives.objectif_limo_ttc)}</p>
                        </div>
                    )}
                    {objectives.objectif_night_ttc > 0 && (
                        <div className="bg-background/50 rounded-lg p-2 text-center">
                            <div className="flex items-center justify-center gap-1 mb-1">
                                <Wine className="w-3 h-3 text-purple-500" />
                                <span className="text-xs text-muted-foreground">Night</span>
                            </div>
                            <p className="font-semibold text-sm">{formatCurrency(objectives.objectif_night_ttc)}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Composant groupes BtoB - Toujours affiché
const BtoBSection = ({ groups, hasBtoB, totalPax }) => {
    return (
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-amber-500" />
                    Groupes du jour (BtoB)
                </h3>
                {hasBtoB && <span className="text-lg font-bold text-amber-600">{totalPax} pax</span>}
            </div>
            
            {hasBtoB && groups?.length > 0 ? (
                <div className="space-y-2">
                    {groups.map((group, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-background/50 rounded-lg p-3">
                            <div>
                                <p className="font-medium">{group.client_name || 'Groupe'}</p>
                            </div>
                            <div className="text-right">
                                <p className="font-bold">{group.pax} pers.</p>
                                {group.tm && <p className="text-xs text-muted-foreground">TM: {formatCurrency(group.tm)}</p>}
                                <p className="text-xs text-muted-foreground capitalize">{group.shift}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground italic">Pas de groupes mis à jour - à voir avec Aurélie</p>
            )}
        </div>
    );
};

// Composant responsables présents (Directeur, Manager, Chef)
const ResponsablesSection = ({ responsables, isConsolidated }) => {
    if (isConsolidated) return null;
    
    if (!responsables || responsables.length === 0) {
        return (
            <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-xl p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-2">
                    <UserCheck className="w-5 h-5 text-cyan-500" />
                    Responsables du jour
                </h3>
                <p className="text-sm text-muted-foreground">Aucun poste à responsabilité configuré</p>
            </div>
        );
    }

    const presents = responsables.filter(r => r.is_present);
    const absents = responsables.filter(r => !r.is_present);

    return (
        <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 rounded-xl p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
                <UserCheck className="w-5 h-5 text-cyan-500" />
                Responsables du jour
            </h3>
            
            {/* Présents */}
            {presents.length > 0 && (
                <div className="mb-3">
                    <p className="text-xs text-green-600 font-medium mb-2 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Présents ({presents.length})
                    </p>
                    <div className="space-y-1">
                        {presents.map((r, idx) => (
                            <div key={idx} className="bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2">
                                <div className="flex items-center gap-2">
                                    {r.department === 'cuisine' ? (
                                        <ChefHat className="w-4 h-4 text-orange-500" />
                                    ) : (
                                        <UtensilsCrossed className="w-4 h-4 text-blue-500" />
                                    )}
                                    <span className="font-medium">{r.name}</span>
                                    <span className="text-xs text-muted-foreground">({r.position})</span>
                                </div>
                                {r.shifts?.length > 0 && (
                                    <div className="text-xs text-muted-foreground mt-1 ml-6">
                                        {r.shifts.map((s, i) => (
                                            <span key={i} className="mr-2">{s.start} - {s.end}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Absents */}
            {absents.length > 0 && (
                <div>
                    <p className="text-xs text-red-600 font-medium mb-2 flex items-center gap-1">
                        <XOctagon className="w-3 h-3" /> Absents ({absents.length})
                    </p>
                    <div className="space-y-1">
                        {absents.map((r, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 opacity-70">
                                <div className="flex items-center gap-2">
                                    {r.department === 'cuisine' ? (
                                        <ChefHat className="w-4 h-4 text-orange-500" />
                                    ) : (
                                        <UtensilsCrossed className="w-4 h-4 text-blue-500" />
                                    )}
                                    <span className="font-medium">{r.name}</span>
                                    <span className="text-xs text-muted-foreground">({r.position})</span>
                                </div>
                                {r.absence_type && (
                                    <span className="text-xs text-red-600">{r.absence_type}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Composant équipes heure de pointe
const PeakHoursSection = ({ peakStaff }) => {
    if (!peakStaff) return null;

    return (
        <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/30 rounded-xl p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-pink-500" />
                Équipes aux heures de pointe
            </h3>
            <div className="grid grid-cols-2 gap-3">
                {/* Midi */}
                <div className="bg-background/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <Sun className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-medium">Midi (12h30)</span>
                    </div>
                    <p className="text-2xl font-bold mb-1">{peakStaff.midi?.total || 0}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <UtensilsCrossed className="w-3 h-3 text-blue-500" />
                            Salle: {peakStaff.midi?.salle || 0}
                        </span>
                        <span className="flex items-center gap-1">
                            <ChefHat className="w-3 h-3 text-orange-500" />
                            Cuisine: {peakStaff.midi?.cuisine || 0}
                        </span>
                    </div>
                </div>
                
                {/* Soir */}
                <div className="bg-background/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                        <Moon className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm font-medium">Soir (20h30)</span>
                    </div>
                    <p className="text-2xl font-bold mb-1">{peakStaff.soir?.total || 0}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <UtensilsCrossed className="w-3 h-3 text-blue-500" />
                            Salle: {peakStaff.soir?.salle || 0}
                        </span>
                        <span className="flex items-center gap-1">
                            <ChefHat className="w-3 h-3 text-orange-500" />
                            Cuisine: {peakStaff.soir?.cuisine || 0}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Composant commentaires du jour et de la veille - Avec modale pour voir en entier
const BadReviewsSection = ({ reviews, hasBadReviews }) => {
    const [selectedReview, setSelectedReview] = useState(null);
    
    const getRatingColor = (rating) => {
        if (rating <= 2) return 'text-red-600 bg-red-100';
        if (rating <= 3) return 'text-orange-600 bg-orange-100';
        return 'text-yellow-600 bg-yellow-100';
    };

    const todayReviews = reviews?.filter(r => r.is_today) || [];
    const yesterdayReviews = reviews?.filter(r => !r.is_today) || [];

    return (
        <>
            <div className="bg-gradient-to-br from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-xl p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Commentaires du jour et de la veille
                </h3>
                
                {hasBadReviews && reviews?.length > 0 ? (
                    <div className="space-y-3 max-h-72 overflow-y-auto">
                        {todayReviews.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-red-600 mb-2">Aujourd'hui ({todayReviews.length})</p>
                                {todayReviews.map((review, idx) => (
                                    <ReviewCard 
                                        key={`today-${idx}`} 
                                        review={review} 
                                        getRatingColor={getRatingColor}
                                        onClick={() => setSelectedReview(review)}
                                    />
                                ))}
                            </div>
                        )}
                        
                        {yesterdayReviews.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-orange-600 mb-2">Hier ({yesterdayReviews.length})</p>
                                {yesterdayReviews.map((review, idx) => (
                                    <ReviewCard 
                                        key={`yesterday-${idx}`} 
                                        review={review} 
                                        getRatingColor={getRatingColor}
                                        onClick={() => setSelectedReview(review)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-sm text-muted-foreground italic">Pas de commentaires publiés</p>
                )}
            </div>

            {/* Modale pour voir l'avis en entier */}
            <Dialog open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <span className={cn("px-2 py-1 rounded text-sm font-bold", getRatingColor(selectedReview?.rating))}>
                                {selectedReview?.rating}/5
                            </span>
                            <span>Avis de {selectedReview?.author}</span>
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4">
                        {/* Métadonnées */}
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                            <span>{selectedReview?.source}</span>
                            <span>{selectedReview?.date}</span>
                        </div>
                        
                        {/* Restaurant */}
                        {selectedReview?.restaurant && (
                            <p className="text-sm text-muted-foreground">📍 {selectedReview.restaurant}</p>
                        )}
                        
                        {/* Contenu complet */}
                        <div className="bg-muted/50 rounded-lg p-4">
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                {selectedReview?.content || "Pas de commentaire texte"}
                            </p>
                        </div>
                        
                        {/* Sous-notes si disponibles */}
                        {selectedReview?.sub_ratings && selectedReview.sub_ratings.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-2">Notes détaillées</p>
                                <div className="flex flex-wrap gap-2">
                                    {selectedReview.sub_ratings.map((sub, idx) => (
                                        <span 
                                            key={idx}
                                            className={cn(
                                                "px-2 py-1 rounded text-xs font-medium",
                                                sub.value <= 2 ? "bg-red-100 text-red-700" :
                                                sub.value <= 3 ? "bg-orange-100 text-orange-700" :
                                                "bg-green-100 text-green-700"
                                            )}
                                        >
                                            {sub.label}: {sub.value}/5
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Lien vers le site source */}
                        {selectedReview?.remote_link && (
                            <a 
                                href={selectedReview.remote_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-primary hover:underline"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Voir sur {selectedReview.source}
                            </a>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

const ReviewCard = ({ review, getRatingColor, onClick }) => (
    <div 
        className="bg-background/50 rounded-lg p-3 mb-2 cursor-pointer hover:bg-background/80 transition-colors"
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
                <span className={cn("px-2 py-0.5 rounded text-sm font-bold", getRatingColor(review.rating))}>
                    {review.rating}/5
                </span>
                <span className="text-sm font-medium">{review.author}</span>
            </div>
            <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{review.source}</span>
                {review.remote_link && (
                    <a 
                        href={review.remote_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-muted-foreground hover:text-primary"
                        title={`Voir sur ${review.source}`}
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                )}
            </div>
        </div>
        {review.content && (
            <p className="text-sm text-muted-foreground line-clamp-2">{review.content}</p>
        )}
        {review.restaurant && (
            <p className="text-xs text-muted-foreground mt-1">📍 {review.restaurant}</p>
        )}
        <p className="text-xs text-primary mt-1">Cliquer pour voir en entier →</p>
    </div>
);

// Composant Vigilance Clients - Résumé intelligent des problèmes du mois
const VigilanceSection = ({ vigilanceData, loading }) => {
    if (loading) {
        return (
            <div className="bg-gradient-to-br from-amber-500/10 to-red-500/10 border border-amber-500/30 rounded-xl p-4">
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                    <ShieldAlert className="w-5 h-5 text-amber-500" />
                    Vigilance Clients (mois en cours)
                </h3>
                <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                </div>
            </div>
        );
    }

    if (!vigilanceData) {
        return null;
    }

    const { negative_count, summary, vigilance_points, problem_themes, reviews, month } = vigilanceData;

    return (
        <div className="bg-gradient-to-br from-amber-500/10 to-red-500/10 border border-amber-500/30 rounded-xl p-4" data-testid="vigilance-clients-card">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
                <ShieldAlert className="w-5 h-5 text-amber-500" />
                Vigilance Clients ({month})
            </h3>
            
            {negative_count === 0 ? (
                <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-5 h-5" />
                    <p className="text-sm font-medium">{summary}</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Compteur et résumé compilé */}
                    <div className="bg-amber-100/50 dark:bg-amber-900/20 rounded-lg p-3">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="bg-amber-500 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                                {negative_count}
                            </div>
                            <div>
                                <p className="font-medium text-amber-700 dark:text-amber-400">
                                    avis à surveiller ce mois
                                </p>
                                <p className="text-xs text-muted-foreground">Notes 1★, 2★ ou 3★</p>
                            </div>
                        </div>
                        {/* Résumé compilé */}
                        <p className="text-sm text-foreground/80 mt-2 leading-relaxed">
                            {summary}
                        </p>
                    </div>

                    {/* Thèmes problématiques avec citations */}
                    {problem_themes && problem_themes.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                                <MessageSquareWarning className="w-3 h-3" />
                                Thèmes récurrents identifiés
                            </p>
                            <div className="space-y-2">
                                {problem_themes.map((theme, idx) => (
                                    <div 
                                        key={idx}
                                        className={cn(
                                            "rounded-lg p-2 text-sm",
                                            theme.severity === 'high' 
                                                ? "bg-red-100/70 dark:bg-red-900/30 border-l-4 border-red-500"
                                                : "bg-orange-100/70 dark:bg-orange-900/30 border-l-4 border-orange-400"
                                        )}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">{theme.theme}</span>
                                            <span className={cn(
                                                "px-2 py-0.5 rounded-full text-xs font-bold",
                                                theme.severity === 'high' 
                                                    ? "bg-red-200 text-red-800"
                                                    : "bg-orange-200 text-orange-800"
                                            )}>
                                                {theme.count}x
                                            </span>
                                        </div>
                                        {theme.example_quote && (
                                            <p className="text-xs text-muted-foreground mt-1 italic">
                                                "{theme.example_quote}"
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Points de vigilance par catégorie (sous-notes) */}
                    {vigilance_points && vigilance_points.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                <ThumbsDown className="w-3 h-3" />
                                Catégories impactées
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {vigilance_points.map((point, idx) => (
                                    <span 
                                        key={idx}
                                        className={cn(
                                            "px-2 py-1 rounded-full text-xs font-medium",
                                            point.severity === 'high' 
                                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                                : point.severity === 'medium'
                                                    ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                                    : "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                                        )}
                                    >
                                        {point.label} ({point.count})
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Sélecteur de restaurant - Style avec fond noir comme sur les autres pages
const RestaurantSelector = ({ restaurants, selectedRestaurant, onSelect, isConsolidated, onToggleConsolidated, canViewConsolidated }) => {
    return (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Sélectionner un restaurant</h2>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                {restaurants.map(resto => {
                    const logoUrl = RESTAURANT_LOGOS[resto.name] || resto.logo_url;
                    const subtitle = RESTAURANT_SUBTITLES[resto.name];
                    const isSelected = selectedRestaurant === resto.name && !isConsolidated;
                    
                    return (
                        <button
                            key={resto._id || resto.name}
                            onClick={() => onSelect(resto.name)}
                            className={cn(
                                "relative h-20 md:h-24 rounded-xl overflow-hidden cursor-pointer transition-all duration-200",
                                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                                "hover:scale-105 hover:shadow-lg",
                                isSelected 
                                    ? "ring-2 ring-primary ring-offset-2 shadow-lg" 
                                    : "ring-1 ring-border"
                            )}
                        >
                            {/* Fond noir */}
                            <div className="absolute inset-0 bg-black" />
                            
                            {logoUrl ? (
                                <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                                    <img 
                                        src={logoUrl} 
                                        alt={resto.name} 
                                        className={cn(
                                            "max-w-full object-contain",
                                            subtitle ? "max-h-[65%]" : "max-h-full"
                                        )}
                                    />
                                    {subtitle && (
                                        <span className="text-white text-xs font-medium mt-1 tracking-wider">
                                            {subtitle}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center p-2">
                                    <span className="text-white text-xs font-bold text-center">
                                        {RESTAURANT_DISPLAY_NAMES[resto.name] || resto.name}
                                    </span>
                                </div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const BriefingPage = () => {
    const { user, isDirection, canViewConsolidated } = useAuth();
    const today = getTodayString();
    
    const [restaurants, setRestaurants] = useState([]);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [isConsolidated, setIsConsolidated] = useState(false);
    const [briefingData, setBriefingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [vigilanceData, setVigilanceData] = useState(null);
    const [vigilanceLoading, setVigilanceLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadRestaurants = async () => {
            try {
                const response = await restaurantsApi.getAll();
                setRestaurants(response.data || []);
                
                if (response.data?.length > 0) {
                    // Ma biche sur le toit par défaut
                    const maBiche = response.data.find(r => r.name === "Ma biche sur le toit");
                    if (maBiche) {
                        setSelectedRestaurant(maBiche.name);
                    } else {
                        setSelectedRestaurant(response.data[0].name);
                    }
                }
            } catch (err) {
                console.error('Erreur chargement restaurants:', err);
                setError('Impossible de charger les restaurants');
            }
        };
        loadRestaurants();
    }, [user]);

    const loadBriefing = useCallback(async () => {
        if (!selectedRestaurant && !isConsolidated) return;
        
        setLoading(true);
        setError(null);
        
        try {
            let response;
            if (isConsolidated) {
                response = await briefingApi.getConsolidated(today);
            } else {
                response = await briefingApi.getBriefing(selectedRestaurant, today);
            }
            setBriefingData(response.data);
        } catch (err) {
            console.error('Erreur chargement briefing:', err);
            setError('Impossible de charger le briefing');
            toast.error('Erreur lors du chargement du briefing');
        } finally {
            setLoading(false);
        }
    }, [selectedRestaurant, isConsolidated, today]);

    // Charger les données de vigilance clients (avis négatifs du mois)
    const loadVigilanceData = useCallback(async () => {
        if (!selectedRestaurant || isConsolidated) {
            setVigilanceData(null);
            return;
        }
        
        setVigilanceLoading(true);
        try {
            const token = localStorage.getItem('token');
            const API_URL = process.env.REACT_APP_BACKEND_URL || '';
            const response = await fetch(
                `${API_URL}/api/myli/vigilance/${encodeURIComponent(selectedRestaurant)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const data = await response.json();
                setVigilanceData(data);
            } else {
                setVigilanceData(null);
            }
        } catch (err) {
            console.error('Erreur chargement vigilance:', err);
            setVigilanceData(null);
        } finally {
            setVigilanceLoading(false);
        }
    }, [selectedRestaurant, isConsolidated]);

    useEffect(() => {
        loadBriefing();
    }, [loadBriefing]);

    useEffect(() => {
        loadVigilanceData();
    }, [loadVigilanceData]);

    const handleSelectRestaurant = (name) => {
        setIsConsolidated(false);
        setSelectedRestaurant(name);
    };

    const handleToggleConsolidated = () => {
        setIsConsolidated(!isConsolidated);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', { 
            weekday: 'long', 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
        });
    };

    return (
        <div className="flex min-h-screen bg-background">
            <Sidebar />
            <div className="flex-1 flex flex-col">
                <Header />
                <main className="flex-1 p-6 overflow-auto">
                    {/* Titre avec date */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-3">
                                <Calendar className="w-7 h-7 text-primary" />
                                Briefing du jour
                            </h1>
                            <p className="text-muted-foreground mt-1 capitalize">{formatDate(today)}</p>
                        </div>
                        <Button onClick={loadBriefing} variant="outline" size="sm" className="gap-2">
                            <RefreshCw className="w-4 h-4" />
                            Actualiser
                        </Button>
                    </div>

                    {/* Sélecteur de restaurant */}
                    <RestaurantSelector
                        restaurants={restaurants}
                        selectedRestaurant={selectedRestaurant}
                        onSelect={handleSelectRestaurant}
                        isConsolidated={isConsolidated}
                        onToggleConsolidated={handleToggleConsolidated}
                        canViewConsolidated={canViewConsolidated}
                    />

                    {/* Message de bienvenue personnalisé */}
                    <div className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-5">
                        <h2 className="text-xl font-bold text-primary">
                            👋 Bonjour {isConsolidated ? 'équipe' : (RESTAURANT_DISPLAY_NAMES[selectedRestaurant] || selectedRestaurant)}, voici vos objectifs de la journée !
                        </h2>
                        <p className="text-muted-foreground mt-1">
                            {formatDate(today)}
                        </p>
                    </div>

                    {/* Contenu du briefing */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[...Array(6)].map((_, i) => (
                                <Skeleton key={i} className="h-48 rounded-xl" />
                            ))}
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                            <p className="text-red-500">{error}</p>
                        </div>
                    ) : briefingData ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {/* Météo */}
                            <WeatherSection weather={briefingData.weather} />

                            {/* VIP du jour */}
                            <VIPSection reservations={briefingData.reservations} />

                            {/* Objectifs CA complets */}
                            <ObjectivesSection objectives={briefingData.sales_objectives} />

                            {/* Groupes BtoB - Toujours affiché */}
                            <BtoBSection 
                                groups={briefingData.btob_groups}
                                hasBtoB={briefingData.has_btob}
                                totalPax={briefingData.btob_total_pax}
                            />

                            {/* Responsables présents */}
                            <ResponsablesSection 
                                responsables={briefingData.responsables} 
                                isConsolidated={isConsolidated}
                            />

                            {/* Équipes heure de pointe */}
                            <PeakHoursSection peakStaff={briefingData.peak_hours_staff} />

                            {/* Commentaires du jour et de la veille - Toujours affiché */}
                            <BadReviewsSection 
                                reviews={briefingData.bad_reviews}
                                hasBadReviews={briefingData.has_bad_reviews}
                            />

                            {/* Vigilance Clients - Résumé des avis négatifs du mois */}
                            {!isConsolidated && (
                                <VigilanceSection 
                                    vigilanceData={vigilanceData}
                                    loading={vigilanceLoading}
                                />
                            )}
                        </div>
                    ) : null}
                </main>
            </div>
        </div>
    );
};

export default BriefingPage;
